import { CSSProperties, useRef } from 'react';
import { toastError, toastSuccess } from 'modules/Toast';
import ButtonSymphony from 'elements/ButtonSymphony';
import Icon from 'elements/Icon';
import Colors from 'modules/Colors';
import useUniversalUploadWidget from 'Hooks/useUniversalUploadWidget';
import { updateOrganizationPartial_api } from 'api/organizationV2Api';
import { track } from 'analytics';

interface ProfilePictureSectionProps {
    /** Current profile picture URL */
    profilePictureUrl?: string;
    /** Organization ID for updating the profile picture */
    organizationId?: number;
    /** Optional custom title */
    title?: string;
    /** Size of the profile picture preview */
    size?: number;
    /** Custom className */
    className?: string;
    /** Custom style */
    style?: CSSProperties;
    /** Whether to show the upload button */
    showUploadButton?: boolean;
    /** Custom upload button text */
    uploadButtonText?: string;
    /** Callback when profile picture is updated */
    onProfilePictureUpdate?: (url: string) => void;
    /** Whether this is a clickable profile picture (for inline editing) */
    isClickable?: boolean;
    /** Placeholder content when no profile picture */
    placeholder?: React.ReactNode;
}

const ProfilePictureSection = ({
    profilePictureUrl,
    organizationId,
    title,
    size = 60,
    className,
    style,
    showUploadButton = true,
    uploadButtonText,
    onProfilePictureUpdate,
    isClickable = false,
    placeholder
}: ProfilePictureSectionProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const {
        uploadFiles,
        isUploading,
        uploadedAssets,
        clearUploadedAssets
    } = useUniversalUploadWidget();

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const file = files[0];
        
        // Validate file type
        if (!file.type.startsWith('image/')) {
            toastError('Please select an image file');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toastError('Image must be smaller than 5MB');
            return;
        }

        handleUpload(file);
    };

    const handleUpload = async (file: File) => {
        try {
            // Clear any previous uploads
            clearUploadedAssets();

            // Create upload file object
            const uploadFile = {
                id: Date.now().toString(),
                file,
                name: file.name,
                size: file.size,
                type: file.type,
                status: 'uploading' as const,
                uploadProgress: 0,
                iconType: 'image' as const
            };

            // Start upload
            await uploadFiles([uploadFile]);

            // Wait for upload to complete and get the result
            const uploadResult = uploadedAssets.find(asset => asset.iconType === 'image');
            
            if (uploadResult?.s3Url) {
                await updateOrganizationProfilePicture(uploadResult.s3Url);
            }

        } catch (error) {
            console.error('Upload failed:', error);
            toastError('Failed to upload profile picture');
        }
    };

    const updateOrganizationProfilePicture = async (imageUrl: string) => {
        if (!organizationId) {
            onProfilePictureUpdate?.(imageUrl);
            return;
        }

        try {
            const result = await updateOrganizationPartial_api(organizationId, {
                profile_picture: imageUrl
            });

            if (result) {
                toastSuccess('Profile picture updated successfully!');
                onProfilePictureUpdate?.(imageUrl);
                
                track('Organization Profile Picture Updated', {
                    organization_id: organizationId,
                    source: 'profile_picture_section'
                });
            } else {
                throw new Error('Failed to update organization');
            }
        } catch (error) {
            console.error('Failed to update organization profile picture:', error);
            toastError('Failed to update profile picture');
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const renderPlaceholder = () => {
        if (placeholder) {
            return placeholder;
        }
        
        return (
            <Icon 
                className="uploadIcon" 
                icon="upload" 
                size={Math.floor(size * 0.4)} 
                color={Colors.greyDark}
            />
        );
    };

    const renderProfilePicture = () => {
        return (
            <div 
                className={`profilePicturePreview ${isClickable ? 'clickable' : ''}`}
                style={{
                    ...sty.profilePicturePreview,
                    width: size,
                    height: size,
                    cursor: isClickable ? 'pointer' : 'default'
                }}
                onClick={isClickable ? handleUploadClick : undefined}
            >
                {profilePictureUrl ? (
                    <img
                        src={profilePictureUrl}
                        alt="Profile"
                        style={sty.profileImage}
                    />
                ) : (
                    renderPlaceholder()
                )}
                
                {isClickable && (
                    <div className="uploadOverlay" style={sty.uploadOverlay}>
                        <Icon className="uploadOverlayIcon" icon="upload" size={20} color={Colors.white} />
                    </div>
                )}
            </div>
        );
    };

    return (
        <div 
            className={`profilePictureSection ${className || ''}`}
            style={{
                ...sty.profilePictureSection,
                ...style
            }}
        >
            {title && (
                <div className="profilePictureSectionTitle" style={sty.title}>
                    {title}
                </div>
            )}
            
            <div className="profilePictureSectionContent" style={sty.content}>
                {renderProfilePicture()}
                
                {showUploadButton && !isClickable && (
                    <ButtonSymphony
                        className="uploadProfilePictureButton"
                        variant="outlined"
                        size="small"
                        onClick={handleUploadClick}
                        isLoading={isUploading}
                        disabled={isUploading}
                    >
                        {isUploading 
                            ? 'Uploading...' 
                            : uploadButtonText || (profilePictureUrl ? 'Change Picture' : 'Upload Picture')
                        }
                    </ButtonSymphony>
                )}
            </div>

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    profilePictureSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    title: {
        fontSize: 14,
        fontWeight: 500,
        color: Colors.textColor,
    },
    content: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    profilePicturePreview: {
        borderRadius: '50%',
        overflow: 'hidden',
        flexShrink: 0,
        backgroundColor: Colors.greyLight,
        border: `2px solid ${Colors.grey}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
    },
    profileImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    uploadOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0,
        transition: 'opacity 0.2s ease',
    },
};

export default ProfilePictureSection;